import React, { useState, useEffect } from 'react';
import { MapPin, Bell, User, LogOut, Heart, Gift, X, HelpCircle, ListChecks, Store, Plus, Pencil, Briefcase, Megaphone } from 'lucide-react';
import { useLocation } from '../context/LocationContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NotificationBadge, NotificationDropdown } from './NotificationList';
import AuthModal from './AuthModal';
import useSiteConfig from '../hooks/useSiteConfig';
import SearchAutocomplete from './SearchAutocomplete';
import { getUserChosenMunicipio, clearUserChosenMunicipio, USER_CHOSEN_MUNICIPIO_EVENT } from '../services/userChosenMunicipio';
import { resolveBrandLogo } from '../utils/brandLogo';
import BackendImage from './BackendImage';
import useMunicipioBasePath from '../hooks/useMunicipioBasePath';
import useMunicipioFeatures from '../hooks/useMunicipioFeatures';
import { api } from '../services/api';

export default function Header() {
  const { locationName, openPrompt } = useLocation();
  const navigate = useNavigate();
  const { slug: tenantSlug, basePath } = useMunicipioBasePath();
  const { preciso_de_enabled: precisoDeEnabled } = useMunicipioFeatures(tenantSlug);
  const { user, logout, isAuthenticated } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { config, loading: configLoading } = useSiteConfig();
  // Negócios do usuário logado — controla os itens contextuais do menu
  // (Cadastrar negócio / Editar meu negócio / Cadastrar mais um negócio).
  const [myBizList, setMyBizList] = useState([]);
  useEffect(() => {
    if (!isAuthenticated) { setMyBizList([]); return; }
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get('/my-businesses');
        if (!cancel) setMyBizList(Array.isArray(data) ? data : []);
      } catch (_) { /* silent */ }
    })();
    return () => { cancel = true; };
  }, [isAuthenticated, user?.id]);
  // Evita exibir o fallback SVG enquanto a config ainda está carregando — assim
  // a logo real do município é a única que aparece (sem flash da "logo de backup").
  const logoSrc = configLoading ? null : resolveBrandLogo(config);

  // Discreet "stuck-to" município indicator. Only renders when user has an
  // explicit choice; tappable × clears it and navigates to / (legacy home).
  const [chosenMunicipio, setChosenMunicipio] = useState(() => getUserChosenMunicipio());
  useEffect(() => {
    const onChange = () => setChosenMunicipio(getUserChosenMunicipio());
    window.addEventListener(USER_CHOSEN_MUNICIPIO_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(USER_CHOSEN_MUNICIPIO_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  const handleClearMunicipio = (e) => {
    e.stopPropagation();
    clearUserChosenMunicipio();
    setChosenMunicipio(null);
    navigate('/');
  };

  return (
    <>
      <header 
        className="sticky top-0 z-50 bg-white border-b border-[var(--border)] shadow-sm"
        data-testid="header"
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div 
            onClick={() => navigate(basePath || '/')}
            className="flex items-center gap-2 cursor-pointer"
            data-testid="logo"
            role="link"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(basePath || '/'); } }}
            aria-label="Ir para a página inicial"
          >
            {logoSrc ? (
              <BackendImage
                src={logoSrc}
                alt="Empreende Comunidade"
                className="h-10 sm:h-12 w-auto object-contain"
                eager
                fallback={<div className="h-10 sm:h-12 w-32 sm:w-40" aria-hidden="true" />}
              />
            ) : (
              <div className="h-10 sm:h-12 w-32 sm:w-40" aria-hidden="true" />
            )}
          </div>

          {/* Search bar global com autocomplete — desktop only */}
          <div className="hidden md:block flex-1 max-w-xl mx-4" data-testid="header-search-wrapper">
            <SearchAutocomplete
              placeholder="Buscar negócios, categorias, bairros…"
              municipioSlug={tenantSlug}
              onSubmit={(term) => {
                navigate(`${basePath}/explorar?busca=${encodeURIComponent(term)}`);
              }}
            />
          </div>

          <div className="flex items-center gap-2">
            {chosenMunicipio ? (
              <div
                className="flex items-center gap-1 text-sm pl-3 pr-1 py-1.5 rounded-lg bg-transparent border border-[var(--primary)] hover:bg-[var(--primary)]/5 transition-colors"
                title={`Você está vendo conteúdo de ${chosenMunicipio.nome}. Clique no × para voltar para a página geral.`}
                data-testid="header-tenant-chip"
              >
                <MapPin className="w-3.5 h-3.5 text-[var(--secondary)]" />
                <span className="text-[var(--text-secondary)] hidden sm:inline truncate max-w-[140px]">
                  {chosenMunicipio.nome}
                </span>
                <button
                  type="button"
                  onClick={handleClearMunicipio}
                  className="p-1 ml-0.5 rounded-md hover:bg-[var(--primary)]/10 text-gray-400 hover:text-[var(--primary)] transition-colors"
                  aria-label={`Sair de ${chosenMunicipio.nome}`}
                  data-testid="header-tenant-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={openPrompt}
                className="flex items-center gap-1.5 text-sm hover:bg-gray-50 px-3 py-1.5 rounded-lg transition-colors"
                data-testid="location-button"
                title="Mudar localização (GPS ou escolher cidade)"
              >
                <MapPin className="w-4 h-4 text-[var(--primary)]" />
                <span className="text-[var(--text-secondary)] hidden sm:inline">{locationName}</span>
              </button>
            )}

            {isAuthenticated ? (
              <>
                {/* Notification Bell */}
                <div className="relative">
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative p-2 hover:bg-gray-50 rounded-full transition-colors"
                    data-testid="notification-bell"
                    aria-label="Notificações"
                  >
                    <Bell className="w-5 h-5 text-[var(--text-secondary)]" />
                    <NotificationBadge />
                  </button>
                  <NotificationDropdown 
                    isOpen={showNotifications} 
                    onClose={() => setShowNotifications(false)} 
                  />
                </div>

                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 hover:bg-gray-50 px-3 py-1.5 rounded-lg transition-colors"
                    data-testid="user-menu-btn"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#56007F] text-white flex items-center justify-center text-sm font-bold">
                      {user?.nome?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <span className="text-sm font-medium text-gray-700 hidden sm:inline max-w-[120px] truncate">
                      {user?.nome?.split(' ')[0] || 'Usuário'}
                    </span>
                  </button>

                  {showUserMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                      <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-lg border z-50 py-1 animate-in fade-in slide-in-from-top-2" data-testid="user-dropdown">
                        <div className="px-4 py-2.5 border-b">
                          <p className="text-sm font-medium truncate">{user?.nome || 'Usuário'}</p>
                          <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                        </div>
                        <button
                          onClick={() => { setShowUserMenu(false); navigate(`${basePath}/complete-seu-perfil`); }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          data-testid="menu-profile"
                        >
                          <User className="w-4 h-4" /> Meu perfil
                        </button>
                        <button
                          onClick={() => { setShowUserMenu(false); navigate(`${basePath}/perfil?tab=favoritos`); }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          data-testid="menu-favorites"
                        >
                          <Heart className="w-4 h-4" /> Meus favoritos
                        </button>
                        <button
                          onClick={() => { setShowUserMenu(false); navigate(`${basePath}/perfil?tab=cupons`); }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          data-testid="menu-coupons"
                        >
                          <Gift className="w-4 h-4" /> Meus cupons
                        </button>
                        <button
                          onClick={() => { setShowUserMenu(false); navigate(`${basePath}/perfil?tab=vagas`); }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          data-testid="menu-vagas"
                        >
                          <Briefcase className="w-4 h-4" /> Minhas Vagas
                        </button>

                        {/* Contextual: Cadastrar / Editar / Cadastrar mais um negócio */}
                        {myBizList.length === 0 && (
                          <button
                            onClick={() => { setShowUserMenu(false); navigate('/cadastrar'); }}
                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#56007F] hover:bg-purple-50 transition-colors font-medium"
                            data-testid="menu-cadastrar-negocio"
                          >
                            <Store className="w-4 h-4" /> Cadastrar negócio
                          </button>
                        )}
                        {myBizList.length === 1 && (
                          <>
                            <button
                              onClick={() => { setShowUserMenu(false); navigate(`/editar-negocio/${myBizList[0].id}`); }}
                              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              data-testid="menu-editar-negocio"
                            >
                              <Pencil className="w-4 h-4" /> Editar meu negócio
                            </button>
                            <button
                              onClick={() => { setShowUserMenu(false); navigate('/cadastrar'); }}
                              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              data-testid="menu-cadastrar-mais-negocio"
                            >
                              <Plus className="w-4 h-4" /> Cadastrar mais um negócio
                            </button>
                          </>
                        )}
                        {myBizList.length >= 2 && (
                          <>
                            <button
                              onClick={() => { setShowUserMenu(false); navigate(`${basePath}/perfil?tab=negocios`); }}
                              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              data-testid="menu-meus-negocios"
                            >
                              <Store className="w-4 h-4" /> Meus negócios ({myBizList.length})
                            </button>
                            <button
                              onClick={() => { setShowUserMenu(false); navigate('/cadastrar'); }}
                              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              data-testid="menu-cadastrar-mais-negocio"
                            >
                              <Plus className="w-4 h-4" /> Cadastrar mais um negócio
                            </button>
                          </>
                        )}
                        {/* Publicar no Feed — só faz sentido se o usuário tem pelo menos
                            um negócio. Se tiver só 1, já leva direto com negocio_id na URL. */}
                        {myBizList.length >= 1 && (
                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              const qs = myBizList.length === 1
                                ? `?negocio_id=${encodeURIComponent(myBizList[0].id)}`
                                : '';
                              navigate(`/empreendedor/feed/novo${qs}`);
                            }}
                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-purple-700 hover:bg-purple-50 transition-colors"
                            data-testid="menu-publicar-feed"
                          >
                            <Megaphone className="w-4 h-4" /> Publicar no feed
                          </button>
                        )}
                        {precisoDeEnabled !== false && (
                          <button
                            onClick={() => { setShowUserMenu(false); navigate(`${basePath}/precisar-de`); }}
                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            data-testid="menu-preciso-de"
                          >
                            <HelpCircle className="w-4 h-4" /> Preciso de…
                          </button>
                        )}
                        {precisoDeEnabled !== false && (
                          <button
                            onClick={() => { setShowUserMenu(false); navigate(`${basePath}/minhas-solicitacoes`); }}
                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            data-testid="menu-minhas-solicitacoes"
                          >
                            <ListChecks className="w-4 h-4" /> Minhas solicitações
                          </button>
                        )}
                        <div className="border-t my-1" />
                        <button
                          onClick={() => { setShowUserMenu(false); logout(); navigate(basePath || '/'); }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          data-testid="menu-logout"
                        >
                          <LogOut className="w-4 h-4" /> Sair
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              /* Login Button */
              <button
                onClick={() => setShowAuthModal(true)}
                className="flex items-center gap-2 bg-[#56007F] hover:bg-[#420060] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                data-testid="header-login-btn"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Entrar</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />
    </>
  );
}
